/**
 * The API client. One place where the wire format meets the app.
 *
 * The server sets a readable `sw_csrf` cookie alongside the httpOnly session cookie;
 * every mutation echoes it back in the `X-CSRF-Token` header. That double-submit check
 * is enforced server-side, so forgetting it here fails loudly rather than silently.
 */

import type { components } from "./api-types";
import { newId } from "./utils";

export type Schemas = components["schemas"];

export type User = Schemas["UserResponse"];
export type Me = Schemas["MeResponse"];
export type Group = Schemas["GroupResponse"];
export type GroupSummary = Schemas["GroupSummaryResponse"];
export type Member = Schemas["MemberResponse"];
export type MemberBalance = Schemas["MemberBalanceResponse"];
export type Balances = Omit<Schemas["BalancesResponse"], "balances" | "settle_up"> & {
  balances: MemberBalance[];
  settle_up: Settlement[];
};
export type Settlement = Schemas["SettlementResponse"];
export type Participant = Schemas["ParticipantResponse"];

/**
 * Fields with server-side defaults come back optional in the generated schema, but
 * FastAPI always serializes them. Pinning them here keeps every call site free of
 * defensive `?? {}` noise.
 */
export type LineItem = Omit<Schemas["LineItemResponse"], "assignee_member_ids"> & {
  assignee_member_ids: number[];
};

export type Transaction = Omit<
  Schemas["TransactionResponse"],
  "ledger" | "participants" | "line_items" | "can_edit"
> & {
  ledger: Record<string, number>;
  participants: Participant[];
  line_items: LineItem[];
  can_edit: boolean;
};

export type TransactionPage = Omit<Schemas["TransactionPageResponse"], "items"> & {
  items: Transaction[];
};

export type Preview = Omit<Schemas["PreviewResponse"], "warnings" | "shares"> & {
  warnings: string[];
  shares: Participant[];
};

export type CreateTransaction = Schemas["CreateTransactionRequest"];
export type Stats = Schemas["StatsResponse"];
export type AuditEntry = Schemas["AuditEntryResponse"];
export type Lock = Schemas["LockResponse"];
export type TransferPreview = Schemas["TransferPreviewResponse"];
export type TransferLink = Schemas["TransferLinkResponse"];
export type DuplicateCandidate = Schemas["DuplicateCandidate"];
export type MemberRole = Schemas["MemberRole"];

export interface ApiErrorBody {
  code: string;
  message: string;
  details?: unknown;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message);
    this.name = "ApiError";
    this.status = status;
    this.code = body.code;
    this.details = body.details;
  }

  get isAuth(): boolean {
    return this.status === 401;
  }

  get isConflict(): boolean {
    return this.status === 409;
  }
}

function csrfToken(): string {
  const match = document.cookie.match(/(?:^|;\s*)sw_csrf=([^;]*)/);
  return match?.[1] ? decodeURIComponent(match[1]) : "";
}

const MUTATIONS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

export interface RequestOptions {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** Sent as `Idempotency-Key`; makes a replayed POST return the original entry. */
  idempotencyKey?: string;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? "GET";
  const headers: Record<string, string> = { ...options.headers };
  if (options.body !== undefined) headers["Content-Type"] = "application/json";
  if (MUTATIONS.has(method)) headers["X-CSRF-Token"] = csrfToken();
  if (options.idempotencyKey) headers["Idempotency-Key"] = options.idempotencyKey;

  const response = await fetch(`/api${path}`, {
    method,
    headers,
    credentials: "same-origin",
    signal: options.signal,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  });

  if (response.status === 204) return undefined as T;

  const isJson = response.headers.get("content-type")?.includes("application/json");
  const payload = isJson ? await response.json() : await response.text();

  if (!response.ok) {
    const body =
      isJson && payload && typeof payload === "object" && "error" in payload
        ? (payload.error as ApiErrorBody)
        : { code: "http_error", message: `Request failed (${response.status})` };
    throw new ApiError(response.status, body);
  }
  return payload as T;
}

export const api = {
  get: <T,>(path: string, signal?: AbortSignal) => request<T>(path, { signal }),
  post: <T,>(path: string, body?: unknown, options: RequestOptions = {}) =>
    request<T>(path, { ...options, method: "POST", body }),
  patch: <T,>(path: string, body?: unknown, options: RequestOptions = {}) =>
    request<T>(path, { ...options, method: "PATCH", body }),
  delete: <T,>(path: string, options: RequestOptions = {}) =>
    request<T>(path, { ...options, method: "DELETE" }),
};

/** Build `?a=1&b=2`, dropping anything undefined. */
export function query(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === "") continue;
    search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : "";
}

/** A client-generated key so an offline replay cannot double-charge. */
export function newIdempotencyKey(): string {
  return newId("k");
}
